import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, articleSchema, personSchema, siteUrl } from "@/lib/schemas";
import { blogPosts, authorProfiles } from "@/mocks/blogPosts";
import { Clock, Tag, User, Calendar } from "lucide-react";

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  const relatedPosts = blogPosts
    .filter((p) => p.id !== post?.id && p.category === post?.category)
    .slice(0, 3);
  const baseUrl = siteUrl;

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <SEO
          title="Article Not Found | ExpertAdvise.in Blog"
          description="The article you are looking for does not exist. Browse our insurance and finance knowledge center."
          noindex
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
          <p className="text-slate-500 mb-4">The article you are looking for does not exist.</p>
          <Link to="/blog" className="text-primary-600 font-semibold hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const pageUrl = `${baseUrl}/blog/${post.slug}`;
  const authorProfile = authorProfiles[post.author];

  const schemas = [
    webpageSchema(post.title, post.excerpt, pageUrl),
    ...(authorProfile
      ? [
          personSchema(authorProfile, `${baseUrl}/blog`),
          articleSchema(
            post.title,
            post.excerpt,
            pageUrl,
            "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&h=630&fit=crop&q=80",
            authorProfile,
            post.date,
            post.date
          ),
        ]
      : [
          articleSchema(
            post.title,
            post.excerpt,
            pageUrl,
            "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&h=630&fit=crop&q=80",
            {
              name: post.author,
              jobTitle: "Insurance Advisor",
              description: `Certified advisor at ExpertAdvise.in specializing in ${post.category}.`,
              image: `${baseUrl}/logo.png`,
              sameAs: [`${baseUrl}/blog`],
              knowsAbout: [post.category],
            },
            post.date,
            post.date
          ),
        ]),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Blog", url: `${baseUrl}/blog` },
      { name: post.title, url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${post.title} | ExpertAdvise.in Blog`}
        description={post.excerpt}
        keywords={post.tags.join(", ")}
        canonical={pageUrl}
        ogUrl={pageUrl}
        ogType="article"
        schemas={schemas}
      />
      <Navbar />

      <article className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Blog", href: "/blog" },
              { name: post.title },
            ]}
            className="mb-4"
          />

          <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
            {post.category}
          </span>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-8 pb-8 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{post.excerpt}</p>
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Key Takeaways</h2>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 font-bold mt-0.5">1.</span>
                  <span>Understanding your coverage needs is the first step to choosing the right plan.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 font-bold mt-0.5">2.</span>
                  <span>Compare claim settlement ratios, not just premiums, when evaluating insurers.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 font-bold mt-0.5">3.</span>
                  <span>Always read the fine print on exclusions, waiting periods, and sub-limits.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 font-bold mt-0.5">4.</span>
                  <span>Consult an independent advisor before making long-term financial commitments.</span>
                </li>
              </ul>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4">{post.content}</p>
            <p className="text-slate-600 leading-relaxed mb-4">
              This article is for informational purposes only and should not be considered as financial advice.
              Please consult a certified financial advisor before making any investment or insurance decisions.
            </p>
            <p className="text-slate-600 leading-relaxed">
              For personalized recommendations tailored to your specific needs, book a free consultation with
              ExpertAdvise.in certified advisors. We analyze your financial situation, goals, and risk appetite
              to recommend the most suitable products from across 25+ leading insurers and fund houses.
            </p>
          </div>

          <div className="flex items-center gap-2 mt-8 pt-6 border-t border-slate-100">
            <Tag className="w-4 h-4 text-slate-400" />
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className="py-10 md:py-14 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedPosts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/blog/${rp.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 hover:border-primary-200 transition-all duration-300 hover:shadow-lg overflow-hidden"
                >
                  <div className="h-36 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <span className="text-primary-300 text-5xl font-bold opacity-30">
                      {rp.category.charAt(0)}
                    </span>
                  </div>
                  <div className="p-5">
                    <span className="bg-primary-50 text-primary-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                      {rp.category}
                    </span>
                    <h3 className="text-sm font-bold text-slate-900 mt-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                      {rp.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      {rp.readTime}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Need Personalized Advice?
          </h2>
          <p className="text-slate-600 mb-6">
            Articles inform. Advisors guide. Book a free consultation to get recommendations tailored to your situation.
          </p>
          <Link
            to="/book-appointment?source=Blog%20Detail%20Page"
            className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
          >
            Book Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}